import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { frostedGlassTransitionProps } from '@lib/utils/frostedPopoverTransition';
import { ButtonBase, Popover } from '@mui/material';
import { ReactNode, useState } from 'react';

type UsagePopoutProps = {
  /** What the trigger shows: a word or an icon. */
  label: ReactNode;
  /** Required when `label` is an icon. */
  ariaLabel?: string;
  triggerClassName: string;
  /** Called when the trigger opens the popout, for the caller's tracking. */
  onOpen: () => void;
  /** Mounted only while open (through the closing animation), so a store it observes fetches on open. */
  children: ReactNode;
};

/** A trigger that opens its content in a frosted popover, or a bottom drawer on mobile. */
export const UsagePopout = ({
  label,
  ariaLabel,
  triggerClassName,
  onOpen,
  children,
}: UsagePopoutProps) => {
  const isMobile = useIsMobile();
  // One open state for both presentations, so crossing the breakpoint while open keeps it open.
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = anchorEl !== null;
  // Content mounts on open and unmounts once the closing animation has finished, in either
  // presentation; the drawer itself stays mounted while closed.
  const [contentMounted, setContentMounted] = useState(false);
  const unmountContent = () => setContentMounted(false);

  const openFrom = (trigger: HTMLElement) => {
    setAnchorEl(trigger);
    setContentMounted(true);
    onOpen();
  };
  const close = () => setAnchorEl(null);

  return (
    <>
      <ButtonBase
        className={triggerClassName}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        onClick={(event) => openFrom(event.currentTarget)}
      >
        {label}
      </ButtonBase>
      {isMobile ? (
        <MobileDrawer
          open={open}
          onClose={close}
          // Swipe-to-open is disabled in MobileDrawer: only the trigger opens it.
          onOpen={() => undefined}
          onClosed={unmountContent}
        >
          {contentMounted && children}
        </MobileDrawer>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            transition: { ...frostedGlassTransitionProps, onExited: unmountContent },
            paper: {
              sx: {
                background: 'rgba(30, 30, 30, 0.85)',
                backdropFilter: 'blur(50px)',
                WebkitBackdropFilter: 'blur(50px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                boxShadow:
                  '0 24px 32px -12px rgba(18, 18, 18, 0.6), 0 8px 24px -8px rgba(0, 0, 0, 0.4)',
                marginTop: '8px',
                overflow: 'hidden',
              },
            },
          }}
        >
          {contentMounted && children}
        </Popover>
      )}
    </>
  );
};
